import MainLayout from "@/components/layout/admin/MainLayout.vue";
import AdvancedLaravelVuePaginate from "advanced-laravel-vue-paginate";
import "advanced-laravel-vue-paginate/dist/advanced-laravel-vue-paginate.css";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import Select2Multiple from "v-select2-multiple-component";
import CONFIG from "@/assets/js/config.custom.js";
import axios from "axios";

export default {
  name: "ReferralManagementList",
  components: {
    MainLayout,
    AdvancedLaravelVuePaginate,
    Loading,
    Select2Multiple,
  },

  data() {
    return {
      noDataMsg: CONFIG.NO_DATA_FOUND,
      isFilterOpen: false,
      isLoading: false,
      isSearchMode: false,
      referralData: {},
      selectedReferralIds: [],
      selectedReferralStatuses: {},
      academicYearOptions: [],
      searchData: {
        referrer_student: null,
        referred_student: null,
        academic_year: null,
        status: null,
      },
      REFERRAL_STATUS: CONFIG.REFERRAL_STATUS,
      REFERRAL_STATUS_TEXT: CONFIG.REFERRAL_STATUS_TEXT,
    };
  },

  computed: {
    hasReferralRows() {
      return Array.isArray(this.referralData.data) && this.referralData.data.length > 0;
    },

    isAllSelected() {
      if (!this.hasReferralRows) {
        return false;
      }

      return this.referralData.data.every(({ id }) => this.selectedReferralIds.includes(id));
    },

    statusOptions() {
      return Object.entries(this.REFERRAL_STATUS_TEXT).map(([id, text]) => ({
        id: Number(id),
        text,
      }));
    },
  },

  methods: {
    /**
     * Build search parameters based on user input
     * @returns {Object}
     */
    buildSearchParams() {
      const params = {};

      if (this.searchData.referrer_student?.trim()) {
        params.referrer_student = this.searchData.referrer_student.trim();
      }

      if (this.searchData.referred_student?.trim()) {
        params.referred_student = this.searchData.referred_student.trim();
      }

      if (this.searchData.academic_year !== null && this.searchData.academic_year !== "") {
        params.academic_year = this.searchData.academic_year;
      }

      if (this.searchData.status !== null && this.searchData.status !== "") {
        params.status = this.searchData.status;
      }

      return params;
    },

    /**
     * Fetch academic years for the dropdown filter
     */
    getAcademicYears() {
      axios
        .get("period/periodList")
        .then((response) => {
          this.academicYearOptions = Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
        });
    },

    /**
     * Fetch referral data and keep selected referral statuses in sync.
     * @param {string} url - The endpoint URL to request
     * @param {Object} [params={}] - Query parameters for the request
     * @returns {void}
     */
    fetchReferralData(url, params = {}) {
      this.isLoading = true;

      axios.get(url, { params })
        .then((response) => {
          this.referralData = response.data;
          this.syncSelectedReferralStatuses();
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            this.$store.commit("logout");
            window.location.replace("login");
            return;
          }
          if (error.response?.status === 422) {
            this.$notification.error(error.response.data.message, {
              timer: 3,
              position: "bottomCenter",
            });
            return;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * Fetch referral list for the current academic year.
     * @param {number} [page=1] - The page number to fetch
     * @returns {void}
     */
    getReferralList(page = 1) {
      this.isSearchMode = false;
      this.fetchReferralData(`/referrals/list?page=${page}`);
    },

    /**
     * Fetch search results for the current filters.
     * @param {number} [page=1] - The page number to fetch
     * @returns {void}
     */
    fetchSearchResults(page = 1) {
      this.fetchReferralData(`/referrals/search?page=${page}`, this.buildSearchParams());
    },

    /**
     * Search referral list using explicit filters only.
     * @returns {void}
     */
    search() {
      this.clearSelectedReferrals();
      this.isSearchMode = true;
      this.fetchSearchResults(1);
    },

    /**
     * Fetch the next page while preserving the current mode.
     * @param {number} [page=1] - The page number to fetch
     * @returns {void}
     */
    handlePaginate(page = 1) {
      if (this.isSearchMode) {
        this.fetchSearchResults(page);
        return;
      }

      this.getReferralList(page);
    },

    /**
     * Reset search filters and fetch the referral list
     */
    reset() {
      this.clearSelectedReferrals();
      this.searchData = {
        referrer_student: null,
        referred_student: null,
        academic_year: null,
        status: null,
      };
      this.isSearchMode = false;
      this.getReferralList(1);
    },

    /**
     * Toggle selection of an individual referral
     * @param {number} id - The referral ID
     * @param {Object} event - The checkbox change event
     * @return {void}
     */
    toggleSelection(id, event) {
      const referralId = Number(id);

      if (event.target.checked) {
        const referral = this.referralData.data.find(
          (currentReferral) => Number(currentReferral.id) === referralId
        );

        if (referral) {
          this.rememberSelectedReferral(referral);
        }
        return;
      }

      this.selectedReferralIds = this.selectedReferralIds.filter(
        (selectedId) => selectedId !== referralId
      );

      if (Object.prototype.hasOwnProperty.call(this.selectedReferralStatuses, referralId)) {
        this.$delete(this.selectedReferralStatuses, referralId);
      }
    },

    /**
     * Toggle selection of all selected referrals
     * @param {Object} event - The checkbox change event
     * @return {Promise<void>}
     */
    async toggleSelectAll(event) {
      if (!event.target.checked) {
        this.clearSelectedReferrals();
        return;
      }

      this.referralData.data.forEach((referral) => {
        this.rememberSelectedReferral(referral);
      });

      const currentPage = Number(this.referralData.current_page) || 1;
      const pages = Array.from(
        { length: Number(this.referralData.last_page) || 1 },
        (_, index) => index + 1
      ).filter((page) => page !== currentPage);

      if (!pages.length) {
        return;
      }

      const url = this.isSearchMode ? "/referrals/search" : "/referrals/list";
      const config = this.isSearchMode ? { params: this.buildSearchParams() } : undefined;

      try {
        const responses = await Promise.all(
          pages.map((page) => axios.get(`${url}?page=${page}`, config))
        );

        responses.forEach(({ data }) => {
          (data?.data || []).forEach((referral) => {
            this.rememberSelectedReferral(referral);
          });
        });
      } catch (error) {
        if (error.response?.status === 401) {
          this.$store.commit("logout");
          window.location.replace("login");
          return;
        }

        if (error.response?.status === 422) {
          this.$notification.error(error.response.data.message, {
            timer: 3,
            position: "bottomCenter",
          });
        }
      }
    },

    /**
     * Get selected referral IDs that can transition to the target status.
     * @param {number} targetStatus - The status that should be applied
     * @returns {number[]}
     */
    getSelectedReferralIdsForStatus(targetStatus) {
      return this.selectedReferralIds.filter((id) => (
        Object.prototype.hasOwnProperty.call(this.selectedReferralStatuses, id)
        && Number(this.selectedReferralStatuses[id]) !== Number(targetStatus)
      ));
    },

    /**
     * Clear all selected referrals and their cached statuses.
     * @returns {void}
     */
    clearSelectedReferrals() {
      this.selectedReferralIds = [];
      this.selectedReferralStatuses = {};
    },

    /**
     * Store a selected referral and its current status.
     * @param {Object} referral - The referral record to persist
     * @returns {void}
     */
    rememberSelectedReferral(referral) {
      const referralId = Number(referral.id);

      if (!this.selectedReferralIds.includes(referralId)) {
        this.selectedReferralIds.push(referralId);
      }

      this.$set(this.selectedReferralStatuses, referralId, Number(referral.status));
    },

    /**
     * Refresh cached statuses for referrals still selected on the current page.
     * @returns {void}
     */
    syncSelectedReferralStatuses() {
      if (!this.hasReferralRows) {
        return;
      }

      this.referralData.data.forEach((referral) => {
        const referralId = Number(referral.id);

        if (this.selectedReferralIds.includes(referralId)) {
          this.$set(this.selectedReferralStatuses, referralId, Number(referral.status));
        }
      });
    },

    /**
     * Show confirmation before approving one or more referrals.
     * @param {number} [count=1] - The number of referrals being approved
     * @returns {boolean}
     */
    confirmApprove(count = 1) {
      const targetText = count === 1
        ? "this referral"
        : `the selected ${count} referral(s)`;

      return window.confirm(`Are you sure you want to approve ${targetText}?`);
    },

    /**
     * Show confirmation before rejecting one or more referrals.
     * @param {number} [count=1] - The number of referrals being rejected
     * @returns {boolean}
     */
    confirmReject(count = 1) {
      const targetText = count === 1
        ? "this referral"
        : `the selected ${count} referral(s)`;

      return window.confirm(`Are you sure you want to reject ${targetText}?`);
    },

    /**
     * Approve a single referral
     * @param {number} id - The referral ID
     */
    approve(id) {
      if (!this.confirmApprove()) {
        return;
      }

      this.isLoading = true;

      axios.post(`/referrals/${id}/approve`)
        .then((response) => {
          if (response.data.success) {
            this.$notification.success(response.data.message, {
              timer: 8,
              position: "bottomCenter",
            });
            this.clearSelectedReferrals();
            this.handlePaginate(this.referralData.current_page || 1);
          }
        })
        .catch((error) => {
          this.$notification.error(error.response?.data?.message || "Unable to approve referral.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.isLoading = false;
        });
    },

    /**
     * Reject a single referral
     * @param {number} id - The referral ID
     */
    reject(id) {
      if (!this.confirmReject()) {
        return;
      }

      this.isLoading = true;

      axios.post(`/referrals/${id}/reject`)
        .then((response) => {
          if (response.data.success) {
            this.$notification.success(response.data.message, {
              timer: 3,
              position: "bottomCenter",
            });
            this.clearSelectedReferrals();
            this.handlePaginate(this.referralData.current_page || 1);
          }
        })
        .catch((error) => {
          this.$notification.error(error.response?.data?.message || "Unable to reject referral.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.isLoading = false;
        });
    },

    /**
     * Batch approve selected referrals
     * @return {void}
     */
    batchApprove() {
      if (!this.selectedReferralIds.length) {
        this.$notification.error("Please select at least one referral.", {
          timer: 8,
          position: "bottomCenter",
        });
        return;
      }

      const actionableReferralIds = this.getSelectedReferralIdsForStatus(
        this.REFERRAL_STATUS.APPROVED
      );

      if (!actionableReferralIds.length) {
        this.$notification.error("There is already approved referrals.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      if (!this.confirmApprove(actionableReferralIds.length)) {
        return;
      }

      this.isLoading = true;

      axios.post("/referrals/batch-approve", {
        referral_ids: actionableReferralIds,
      })
        .then((response) => {
          if (response.data.success) {
            this.$notification.success(response.data.message, {
              timer: 3,
              position: "bottomCenter",
            });
            this.clearSelectedReferrals();
            this.handlePaginate(this.referralData.current_page || 1);
          }
        })
        .catch((error) => {
          this.$notification.error(
            error.response?.data?.message || "Unable to batch approve referrals.",
            {
              timer: 3,
              position: "bottomCenter",
            }
          );
          this.isLoading = false;
        });
    },

    /**
     * Batch reject selected referrals
     * @return {void}
     */
    batchReject() {
      if (!this.selectedReferralIds.length) {
        this.$notification.error("Please select at least one referral.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      const actionableReferralIds = this.getSelectedReferralIdsForStatus(
        this.REFERRAL_STATUS.REJECTED
      );

      if (!actionableReferralIds.length) {
        this.$notification.error("There is already rejected referrals.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      if (!this.confirmReject(actionableReferralIds.length)) {
        return;
      }

      this.isLoading = true;

      axios.post("/referrals/batch-reject", {
        referral_ids: actionableReferralIds,
      })
        .then((response) => {
          if (response.data.success) {
            this.$notification.success(response.data.message, {
              timer: 3,
              position: "bottomCenter",
            });
            this.clearSelectedReferrals();
            this.handlePaginate(this.referralData.current_page || 1);
          }
        })
        .catch((error) => {
          this.$notification.error(
            error.response?.data?.message || "Unable to batch reject referrals.",
            {
              timer: 3,
              position: "bottomCenter",
            }
          );
          this.isLoading = false;
        });
    },

    /**
     * Resolve display text for a referral status value.
     * @param {number|string} status - The raw referral status
     * @returns {string}
     */
    getStatusText(status) {
      return this.REFERRAL_STATUS_TEXT[Number(status)] || "-";
    },

    /**
     * Resolve the badge class for a referral status value.
     * @param {number|string} status - The raw referral status
     * @returns {string|undefined}
     */
    getStatusClass(status) {
      switch (Number(status)) {
        case this.REFERRAL_STATUS.PENDING:
          return "bg-warning";
        case this.REFERRAL_STATUS.PROCESSED:
          return "bg-success";
      }
    },
  },

  mounted() {
    this.getAcademicYears();
    this.getReferralList();
  },
};
